import omitDeep from 'omit-deep-lodash';
import get from 'lodash/get';
import set from 'lodash/set';
import setWith from 'lodash/setWith';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import isObject from 'lodash/isObject';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { persistReducer } from 'redux-persist';

import * as constants from '../constants';
import { createReducer } from '../../../utils';

import getCollegeService from '../hemServices/get-college-service';
import filterStatesByCountry from '../states-filter';
import { basicInfoPersistConfig } from '../../../persistConfig';

const initialState = {
  overview: {},
  studies: {},
  admissions: {},
  studentLife: {},
  costs: {},
  transferInfo: {},
  isLoaded: false,
  hasRegisteredChanges: false,
  isInvalid: false,
  initialStateBackup: {},
};

const asJson = (obj) => JSON.stringify(obj, (k, v) => (v === undefined ? '::undefined::' : v), 2).replace(new RegExp('"::undefined::"', 'g'), 'undefined');
const clean = (obj) => omitDeep(JSON.parse(JSON.stringify(omit(obj, [
  'hasRegisteredChanges',
  'initialStateBackup',
  'isLoaded',
  'isInvalid',
]))), 'showError');

export function hasUpdates(state) {
  if (!state) return false;
  const currentState = clean(state);
  const originalState = clean(state.initialStateBackup);
  const modified = !isEqual(currentState, originalState);

  if (process.env.NODE_ENV !== 'production') {
    if (modified) {
      const { detailedDiff: diff } = require('deep-object-diff'); // eslint-disable-line global-require
      console.log(asJson(diff(originalState, currentState))); // eslint-disable-line no-console
    }
  }
  return modified;
}

const reducer = createReducer(initialState, {
  [constants.SET_INITIAL_BASIC_INFO_STATE]: (
    state,
    { data, isLoaded = true, hasRegisteredChanges = false },
  ) => ({
    ...state,
    ...data,
    isLoaded,
    hasRegisteredChanges,
    initialStateBackup: {
      ...cloneDeep(data),
    },
  }),
  [constants.ADD_BASIC_INFO_FIELD_DATA]: (
    state,
    { fieldName, value, fieldKey, hasRegisteredChanges = true },
  ) => {
    const newState = { ...state };
    const localFieldName = fieldKey ? `${fieldName}.${fieldKey}` : fieldName;
    const oldValue = get(state, localFieldName, null);

    const newValue = [
      ...oldValue,
      value,
    ];

    set(newState, localFieldName, newValue);

    return {
      ...newState,
      hasRegisteredChanges,
    };
  },
  [constants.REMOVE_BASIC_INFO_FIELD_DATA]: (
    state,
    { fieldName, order, hasRegisteredChanges = true },
  ) => {
    const newState = { ...state };
    const oldValue = get(state, fieldName, null);
    const newValue = [
      ...oldValue.slice(0, order),
      ...oldValue.slice(order + 1),
    ];

    set(newState, fieldName, newValue);

    return {
      ...newState,
      hasRegisteredChanges,
    };
  },
  [constants.SET_BASIC_INFO_LOADED_STATE]: (
    state,
    { isLoaded },
  ) => ({
    ...state,
    isLoaded,
    hasRegisteredChanges: false,
  }),
  [constants.UPDATE_BASIC_INFO_FIELD_DATA]: (
    state,
    { fieldName, value },
  ) => {
    const newState = { ...state };
    const oldValue = get(state, fieldName, null);
    const isArray = Array.isArray(oldValue);
    let newValue;

    if (isArray) {
      newValue = oldValue.map((item, index) => {
        if (index !== value.order) {
          return item;
        }
        const nestedFields = value.name.split('.');
        if (nestedFields.length === 2) {
          return {
            ...item,
            [nestedFields[0]]: {
              ...item[nestedFields[0]],
              [nestedFields[1]]: value.value,
            },
          };
        } else if (nestedFields.length === 3) {
          return {
            ...item,
            [nestedFields[0]]: {
              ...item[nestedFields[0]],
              [nestedFields[1]]: {
                ...item[nestedFields[0]][nestedFields[1]],
                [nestedFields[2]]: value.value,
              },
            },
          };
        }
        return {
          ...item,
          [value.name]: value.value,
          // MATCH-8143 fix for application factors mapping
          field_he_admissionfactortype: value.field_he_admissionfactortype,
          field_he_importance: value.field_he_importance,
          // for recomended courses
          field_he_prerequisitetype: value.field_he_prerequisitetype,
          // fix for deadlines
          typeName: value.typeName,
        };
      });
    } else {
      newValue = isObject(oldValue) ? {
        ...oldValue,
        ...value,
      } : value;
    }
    setWith(newState, fieldName, newValue, clone);

    return {
      ...newState,
      hasRegisteredChanges: hasUpdates(newState),
    };
  },
  [constants.UPDATE_BASIC_INFO_OPTIONS]: (
    state,
    { fieldName, value },
  ) => {
    const collegeService = getCollegeService();

    if (collegeService) {
      const { cmsDataMapping: { countriesVocabulary, statesVocabulary } } = collegeService;
      const filteredStates = filterStatesByCountry(value, countriesVocabulary, statesVocabulary);
      const newState = { ...state };

      set(newState, `${fieldName}.options`, filteredStates);

      return newState;
    }

    return state;
  },
  [constants.SET_BASIC_INFO_IS_INVALID]: (
    state,
    { isInvalid },
  ) => ({
    ...state,
    isInvalid,
  }),
  [constants.RESET_BASIC_INFO]: (
    state,
  ) => {
    const data = state.initialStateBackup;
    const collegeService = getCollegeService();

    /* istanbul ignore else */
    if (collegeService) {
      collegeService.reset();
    }

    return {
      ...state,
      ...cloneDeep(data),
      isLoaded: true,
      hasRegisteredChanges: false,
    };
  },
});

export default persistReducer(basicInfoPersistConfig, reducer);
